import { useEffect } from 'react';

import { RecordState } from '@gearbelt/global';
import { buttonVariants } from '@gearbelt/ui/components';
import { GearbeltLoader } from '@gearbelt/ui/icons';
import { useQuery } from '@tanstack/react-query';
import { isIOS, isMobileSafari, osVersion } from 'react-device-detect';
import { useNavigate, useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import logo from '@/assets/sitemate.svg';
import excavatorVector from '@/assets/vectors/excavator.svg';
import qrScan from '@/assets/vectors/qr-scan.svg';
import { cn } from '@/lib/utils';
import { getAWAQRCodeQueryOptions } from '@/queries/awa';
import { AWAApiV2Service } from '@/services/awa-api-v2';

export const AWAQRPage = () => {
  const showBufferPage = isIOS && isMobileSafari && parseFloat(osVersion) > 17.2;
  const { qrId } = useParams();
  const [searchParams] = useSearchParams();
  const fromBufferPage = searchParams.get('fromBufferPage');
  const workspaceId = searchParams.get('ws');

  const navigate = useNavigate();

  const { data: qrCodeResponse, isLoading: qrCodeDataLoading } = useQuery(getAWAQRCodeQueryOptions({ qrId, track: !fromBufferPage }));

  const qrCodeData = qrCodeResponse?.qrcode;
  const assetState = qrCodeResponse?.assetState;

  useEffect(() => {
    if (showBufferPage && !fromBufferPage) return;

    (async () => {
      if (!qrCodeDataLoading) {
        if (!qrCodeData && !workspaceId) {
          navigate('/404');
          return;
        }

        if (assetState && assetState !== RecordState.Active) {
          return;
        }

        const workspaceData = workspaceId ? (await (await AWAApiV2Service.GetPublicWorkspaceById(workspaceId)).json()).workspace : null;

        const notAssigned = qrCodeData ? !qrCodeData?.assetId : true;
        const orgId = qrCodeData?.orgId ?? workspaceData?.id;

        try {
          const destinationUrl = notAssigned ? `qr/${qrId}/assign` : `assets/${qrCodeData?.assetId}`;

          navigate(`/${orgId}/${destinationUrl}`);
        } catch (err) {
          if (err instanceof Error) {
            navigate('/error');
          }
        }
      }
    })();
  }, [qrCodeData, qrCodeDataLoading, navigate, showBufferPage, fromBufferPage, qrId, workspaceId, assetState]);

  if (assetState && assetState !== RecordState.Active) {
    return (
      <div className="bg-grey-1 flex h-screen w-screen flex-col items-center justify-center">
        <div className="flex h-[236px] w-[263.23px] flex-col items-center justify-center gap-4">
          <img src={excavatorVector} alt="Excavator" className="h-[137px] w-[263.23px]" />
          <div className="flex flex-col items-center justify-center gap-2">
            <h1 className="text-[18px] leading-normal font-medium">
              {assetState === RecordState.Archived ? 'Asset has been archived' : 'Asset not available'}
            </h1>
            {assetState === RecordState.Archived ? (
              <p className="text-center text-sm">
                This asset could not be accessed <br /> as it has been archived by your admin.
              </p>
            ) : (
              <p className="text-center text-sm">
                This asset may have been removed. <br /> Contact your admin for more assistance.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {!showBufferPage || fromBufferPage ? (
        <div className="flex h-screen w-screen items-center justify-center">
          <GearbeltLoader size={32} />
        </div>
      ) : (
        <div className="flex h-svh flex-col gap-9 pt-8 pb-4">
          <div className="flex flex-col gap-9">
            <div className="mx-auto px-6">
              <img src={logo} alt="Sitemate" className="h-[60px]" />
            </div>
            <span className="text-secondary-text text-center">Your digital ID for use on site.</span>
          </div>

          <div className="min-h-0">
            <div className="h-full max-h-[473px]">
              <img src={qrScan} alt="QR Scan" className="h-full w-full object-cover sm:object-contain" />
            </div>
          </div>

          <a
            className={cn(buttonVariants({ variant: 'default' }), 'mx-auto h-[70px] w-[350px] text-xl')}
            href={`x-safari-${window.location.origin}/qr/${qrId}?${workspaceId ? `ws=${workspaceId}&` : ''}fromBufferPage=true`}>
            Start
          </a>
        </div>
      )}
    </>
  );
};
